import { useCallback, useMemo } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useAppSelector } from 'src/utils/Hooks';
import { useDorisApplyEntityFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';
import { getAppName } from 'src/utils/app';
import { buildDorisPayload, buildDorisPayloadWithInterceptors } from './buildDorisPayload';

export interface WeekRange {
  startWeekId: number;
  endWeekId: number;
}

export interface TimeRange {
  customDate?: WeekRange;
  lastweek?: WeekRange;
  last4weeks?: WeekRange;
  last13weeks?: WeekRange;
  last26weeks?: WeekRange;
  last52weeks?: WeekRange;
  lastYear?: WeekRange;
  ytd?: WeekRange;
  sortBy?: string;
}

export interface UseDorisDataProps {
  /**
   * API method name to call (e.g., 'GetTrafficByType', 'keymetrics')
   */
  methodName: string;

  /**
   * Metric type identifier (e.g., 'ClicksSummary')
   */
  metricType: string;

  /**
   * Field name to retrieve values for (e.g., 'keyword', 'brand')
   */
  fieldName?: string;

  /**
   * Maximum number of results to return
   */
  resultCount?: number;

  /**
   * Query key identifier for caching purposes
   */
  queryKey?: string;

  /**
   * Optional brand IDs to filter by
   */
  brandIds?: number[];

  /**
   * Optional category IDs to filter by
   */
  categoryIds?: number[];

  /**
   * Optional subcategory IDs to filter by
   */
  subcategoryIds?: number[];

  /**
   * Optional segment IDs to filter by
   */
  segmentIds?: string[][];

  /**
   * Minimum retail price filter
   */
  minRetailPrice?: number;

  /**
   * Branding type filter
   */
  branding?: 'branded' | 'unbranded' | 'all';

  /**
   * Maximum retail price filter
   */
  maxRetailPrice?: number;

  /**
   * Keywords to filter by
   */
  keywords?: string[];

  /**
   * Topics to filter by
   */
  topicIds?: string[];

  /**
   * Business unit IDs to filter by
   */
  businessunitids?: string[];

  /**
   * Whether the query is enabled
   */
  enabled?: boolean;

  /**
   * Additional parameters to include in the request
   */
  additionalParams?: Record<string, any>;

  /**
   * Custom time periods to use instead of from Redux state
   */
  customTimePeriods?: {
    startWeekId?: number;
    endWeekId?: number;
    comparisonStartWeekId?: number;
    comparisonEndWeekId?: number;
  };

  /**
   * Whether to skip fetching comparison data
   */
  skipComparison?: boolean;

  /**
   * Options to pass to the useQuery hook
   */
  queryOptions?: {
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
    retry?: boolean | number;
  };

  /**
   * Time range configuration with different period options
   */
  timeRange?: TimeRange;

  /**
   * Trend IDs to filter by, this is used for micro trends, not used for other queries
   */
  trendIds?: string[];
}

const cleanPayload = (payload: Record<string, any>) => {
  const cleaned: Record<string, any> = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      value = [...value].sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') return a - b;
        return String(a).localeCompare(String(b));
      });
    }
    cleaned[key] = value;
  });
  return cleaned;
};

const stableStringify = (obj: Record<string, any>) => {
  const sortedEntries = Object.entries(obj).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(Object.fromEntries(sortedEntries));
};

export const useDorisData = <T = any>({
  methodName,
  metricType,
  fieldName,
  resultCount = 100,
  queryKey,
  enabled = true,
  skipComparison = false,
  queryOptions = {},
  additionalParams = {},
  brandIds,
  categoryIds,
  subcategoryIds,
  segmentIds,
  minRetailPrice,
  maxRetailPrice,
  keywords,
  topicIds,
  businessunitids,
  customTimePeriods,
  branding,
  trendIds,
  timeRange
}: UseDorisDataProps) => {
  const applyEntityFilters = useDorisApplyEntityFilters();

  const defaultMainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const defaultComparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const retailerId = useAppSelector((state) => state.retailer?.id);

  const startWeekId = customTimePeriods?.startWeekId ?? defaultMainTimePeriod.startWeek;
  const endWeekId = customTimePeriods?.endWeekId ?? defaultMainTimePeriod.endWeek;
  const comparisonStartWeekId = customTimePeriods?.comparisonStartWeekId ?? defaultComparisonTimePeriod.startWeek;
  const comparisonEndWeekId = customTimePeriods?.comparisonEndWeekId ?? defaultComparisonTimePeriod.endWeek;

  const baseParams = useMemo(
    () => ({
      metricType,
      fieldName,
      retailerId: +retailerId,
      categoryIds,
      subcategoryIds,
      brandIds,
      segmentIds,
      minRetailPrice,
      maxRetailPrice,
      keywords,
      topicIds,
      businessunitids,
      resultCount,
      branding,
      timeRange,
      trendIds
    }),
    [
      metricType,
      fieldName,
      retailerId,
      categoryIds,
      subcategoryIds,
      brandIds,
      segmentIds,
      minRetailPrice,
      maxRetailPrice,
      keywords,
      topicIds,
      businessunitids,
      resultCount,
      branding,
      timeRange,
      trendIds
    ]
  );

  const primaryRequest = useMemo(
    () =>
      buildDorisPayloadWithInterceptors(
        baseParams,
        additionalParams,
        applyEntityFilters,
        +retailerId,
        startWeekId,
        endWeekId
      ),
    [baseParams, additionalParams, applyEntityFilters, retailerId, startWeekId, endWeekId]
  );

  const comparisonRequest = useMemo(() => {
    if (skipComparison || additionalParams.skipComparison) return null;
    const { interceptComparisonParams, ...restAdditionalParams } = additionalParams;
    const standardPayload = buildDorisPayload(
      baseParams,
      restAdditionalParams,
      applyEntityFilters,
      +retailerId,
      comparisonStartWeekId,
      comparisonEndWeekId
    );
    return interceptComparisonParams && typeof interceptComparisonParams === 'function'
      ? interceptComparisonParams(standardPayload)
      : standardPayload;
  }, [
    skipComparison,
    baseParams,
    additionalParams,
    applyEntityFilters,
    retailerId,
    comparisonStartWeekId,
    comparisonEndWeekId
  ]);

  const constructUrl = useCallback(
    (isComparison = false) => {
      const idSuffix = isComparison ? '_comparison' : '';
      return `api/${getAppName()}/${methodName}?id=${metricType}_${
        fieldName || ''
      }_${startWeekId}_${endWeekId}${idSuffix}`;
    },
    [methodName, metricType, fieldName, startWeekId, endWeekId]
  );

  const fetchPrimaryData = useCallback(async () => {
    const url = constructUrl();
    const response = await axios.post(url, primaryRequest);
    return response.data;
  }, [constructUrl, primaryRequest]);

  const fetchComparisonData = useCallback(async () => {
    if (!comparisonRequest) return null;
    const url = constructUrl(true);
    const response = await axios.post(url, comparisonRequest);
    return response.data;
  }, [constructUrl, comparisonRequest]);

  const cleanedPrimaryRequest = useMemo(() => cleanPayload(primaryRequest), [primaryRequest]);
  // Include the queryKey parameter in React Query's cache key to allow external control of cache invalidation
  // This is crucial for scenarios like segment processing where the payload stays the same but status changes
  const primaryQueryKey = useMemo(
    () => [queryKey, methodName, metricType, fieldName, stableStringify(cleanedPrimaryRequest)].filter(Boolean),
    [queryKey, methodName, metricType, fieldName, cleanedPrimaryRequest]
  );

  const cleanedComparisonRequest = useMemo(
    () => (comparisonRequest ? cleanPayload(comparisonRequest) : null),
    [comparisonRequest]
  );
  const comparisonQueryKey = useMemo(
    () =>
      cleanedComparisonRequest
        ? ['comparison', queryKey, methodName, metricType, fieldName, stableStringify(cleanedComparisonRequest)].filter(
            Boolean
          )
        : null,
    [queryKey, methodName, metricType, fieldName, cleanedComparisonRequest]
  );

  const {
    data: primaryData,
    isLoading: isPrimaryLoading,
    error: primaryError
  } = useQuery<T>({
    queryKey: primaryQueryKey,
    queryFn: fetchPrimaryData,
    enabled: enabled && !!retailerId,
    refetchOnWindowFocus: queryOptions.refetchOnWindowFocus ?? false,
    staleTime: queryOptions.staleTime ?? 60 * 10 * 1000,
    retry: queryOptions.retry ?? 1
  });

  const {
    data: comparisonData,
    isLoading: isComparisonLoading,
    error: comparisonError
  } = useQuery<T>({
    queryKey: comparisonQueryKey,
    queryFn: fetchComparisonData,
    enabled: enabled && !skipComparison && !!retailerId && !!comparisonRequest,
    refetchOnWindowFocus: queryOptions.refetchOnWindowFocus ?? false,
    staleTime: queryOptions.staleTime ?? 60 * 10 * 1000,
    retry: queryOptions.retry ?? 1
  });

  return {
    data: primaryData,
    comparisonData: skipComparison ? null : comparisonData,
    isLoading: isPrimaryLoading || (!skipComparison && isComparisonLoading),
    error: primaryError || (!skipComparison && comparisonError),
    requestParams: {
      primary: primaryRequest,
      comparison: comparisonRequest
    }
  };
};
